import strings from 'l10n'
import { CommissionBasedOn } from 'types/commissionTemplates'
import { DropDownOptions } from 'types/common'
import { CompanyEmployeeEnum, IndustryType } from 'types/company'
export { currencyDropDownOptions, getEnabledCurrencyDropDownOptions, isCurrencyDropDownOptions } from './currencies'
const quickbooksEnabled = process.env.REACT_APP_FEATURE_QUICKBOOKS?.toLowerCase() === 'true'

export const documentTypeDropDownOptions = (
  noaBasedOnClient: boolean,
  alphabeticalOrder: boolean = false
): DropDownOptions[] => {
  let result: DropDownOptions[] = [
    { label: 'Invoice', value: 'INVOICE' },
    { label: 'Payment', value: 'PAYMENT' },
    { label: 'Disbursement', value: 'DISBURSEMENT' },
  ]
  if (noaBasedOnClient) {
    result.push(
      { label: 'Notice of Assignment', value: 'NOA' },
      { label: 'Legal Disclosure', value: 'LEGAL_DISCLOSURE' }
    )
  }
  result = result.concat([
    { label: 'Proof of Insurance', value: 'POI' },
    { label: 'Uniform Commercial Code (UCC)', value: 'UCC' },
    { label: 'Contract', value: 'CONTRACT' },
    { label: 'Tax Doc', value: 'TAX_DOC' },
    { label: 'Letter of Release', value: 'LETTER_OF_RELEASE' },
    { label: 'Buy out Details', value: 'BUY_OUT_DETAILS' },
    { label: 'Debtor Release', value: 'DEBTOR_RELEASE' },
    { label: 'Client termination Release', value: 'CLIENT_TERMINATION_RELEASE' },
    { label: 'Loan Docs', value: 'LOAN_DOCS' },
    { label: 'Legal Docs', value: 'LEGAL_DOCS' },
    { label: 'IRS Docs', value: 'IRS_DOCS' },
    { label: 'PO Docs', value: 'PO_DOCS' },
    { label: 'Personal client info', value: 'PERSONAL_CLIENT_INFO' },
    { label: 'Collections & Payment Issues', value: 'COLLECTIONS_PAYMENT_ISSUES' },
    { label: 'Credit Safe Report', value: 'CREDIT_SAFE_REPORT' },
    { label: 'Other', value: 'OTHER' },
  ])

  if (alphabeticalOrder) {
    result.sort((a, b) => a.label.localeCompare(b.label))
  }
  return result
}

export const documentTypeDropDownOptionsForDebtor: DropDownOptions[] = [
  { label: 'Payment', value: 'PAYMENT' },
  { label: 'Tax Doc', value: 'TAX_DOC' },
  { label: 'Other', value: 'OTHER' },
]

export const isDocumentTypeValueValid = (value: string, noaBasedOnClient: boolean): boolean => {
  const options = documentTypeDropDownOptions(noaBasedOnClient)
  return options.some((option) => option.value === value)
}

export const comprehensiveDocumentTypeDropDownOptions = (alphabeticalOrder: boolean = false): DropDownOptions[] => {
  const baseOptions: DropDownOptions[] = [
    { label: 'Invoice', value: 'INVOICE' },
    { label: 'Payment', value: 'PAYMENT' },
    { label: 'Notice of Assignment', value: 'NOA' },
    { label: 'Monthly statement', value: 'MONTHLY_STATEMENT' },
    { label: 'Disbursement', value: 'DISBURSEMENT' },
    { label: 'Proof of Insurance', value: 'POI' },
    { label: 'Uniform Commercial Code (UCC)', value: 'UCC' },
    { label: 'Contract', value: 'CONTRACT' },
    { label: 'Tax Doc', value: 'TAX_DOC' },
    { label: 'Letter of Release', value: 'LETTER_OF_RELEASE' },
    { label: 'Buy out Details', value: 'BUY_OUT_DETAILS' },
    { label: 'Debtor Release', value: 'DEBTOR_RELEASE' },
    { label: 'Client termination Release', value: 'CLIENT_TERMINATION_RELEASE' },
    { label: 'Loan Docs', value: 'LOAN_DOCS' },
    { label: 'Legal Docs', value: 'LEGAL_DOCS' },
    { label: 'IRS Docs', value: 'IRS_DOCS' },
    { label: 'PO Docs', value: 'PO_DOCS' },
    { label: 'Personal client info', value: 'PERSONAL_CLIENT_INFO' },
    { label: 'Collections & Payment Issues', value: 'COLLECTIONS_PAYMENT_ISSUES' },
    { label: 'Credit Safe Report', value: 'CREDIT_SAFE_REPORT' },
    { label: 'Invoice NOA', value: 'INVOICE_NOA' },
    { label: 'Fuel Advance', value: 'FUEL_ADVANCE' },
    { label: 'Out of System Invoice', value: 'OUT_OF_SYSTEM_INVOICE' },
    { label: 'Remittance', value: 'REMITTANCE' },
    { label: 'Import CSV', value: 'IMPORT_CSV' },
    { label: 'Report Export', value: 'REPORT_EXPORT' },
    { label: 'Ansonia', value: 'ANSONIA' },
    { label: 'Legal Disclosure', value: 'LEGAL_DISCLOSURE' },
    { label: 'Over Advance', value: 'OVER_ADVANCE' },
    { label: 'Other', value: 'OTHER' },
  ]

  if (alphabeticalOrder) {
    baseOptions.sort((a, b) => a.label.localeCompare(b.label))
  }

  return baseOptions
}

export const isComprehensiveDocumentTypeValueValid = (value: string): boolean => {
  const options = comprehensiveDocumentTypeDropDownOptions()
  return options.some((option) => option.value === value)
}

export const companyEmployeeRoleDropDownOptions = (companyType?: string): DropDownOptions[] => {
  const collectionAgent = {
    value: CompanyEmployeeEnum.COLLECTION_AGENT,
    label: strings[CompanyEmployeeEnum.COLLECTION_AGENT],
  }
  if (companyType === 'Debtor') return [collectionAgent]
  return [
    { value: CompanyEmployeeEnum.ACCOUNT_EXECUTIVE, label: strings[CompanyEmployeeEnum.ACCOUNT_EXECUTIVE] },
    collectionAgent,
    { value: CompanyEmployeeEnum.SALES_EXECUTIVE, label: strings[CompanyEmployeeEnum.SALES_EXECUTIVE] },
  ]
}

export const bankAccountTypeDropDownOptions: DropDownOptions[] = [
  { value: '', label: '-' },
  { value: 'CHECKING', label: 'Checking' },
  { value: 'SAVINGS', label: 'Savings' },
]

export const verificationInvoiceDropDownOptions: DropDownOptions[] = [
  { label: '-', value: '' },
  { label: strings.VERIFIED, value: 'VERIFIED' },
  { label: strings.DENIED, value: 'DENIED' },
  { label: strings.PENDING.SINGLE, value: 'PENDING' },
]

export const commissionBasedOnOptions: DropDownOptions[] = [
  { label: strings.FUNDED_INVOICES, value: CommissionBasedOn.FUNDED_INVOICES },
  { label: strings.CLOSE_INVOICES, value: CommissionBasedOn.CLOSED_INVOICES },
]

export const typePaymentTypeDropDownOptions: DropDownOptions[] = [
  { label: strings.ACH, value: 'ACH' },
  { label: strings.CHECK, value: 'CHECK' },
  { label: strings.WIRE, value: 'WIRE' },
]

export const perDaysDropDownOptions: DropDownOptions[] = [
  { label: '360', value: 'DAYS_360' },
  { label: '365', value: 'DAYS_365' },
]

export const typeDaysDropDownOptions: DropDownOptions[] = [
  { label: strings.CALENDAR_DAYS, value: 'CALENDAR_DAYS' },
  { label: strings.BUSINESS_DAYS, value: 'BUSINESS_DAYS' },
  { label: strings.MONDAY_FRIDAY, value: 'MONDAY_FRIDAY' },
]

export const verifyDropDownOptions: DropDownOptions[] = [
  { label: strings.VERIFY_STATUS_DENIED, value: 'DENIED' },
  { label: strings.VERIFY_STATUS_VERIFIED, value: 'VERIFIED' },
  { label: strings.VERIFY_STATUS_NOT_VERIFIED, value: 'NOT_VERIFIED' },
  { label: strings.VERIFY_STATUS_PENDING, value: 'PENDING' },
]

export const debitOrCreditDropdownOptions: DropDownOptions[] = [
  { value: 'INCREASE', label: 'Increase' },
  { value: 'DECREASE', label: 'Decrease' },
]

export type ledgersTypeDropdownOptionsProps = {
  canCreateWriteOffs?: boolean
  canDisplayOverAdvances?: boolean
}

export const ledgersTypeDropdownOptions = (props: ledgersTypeDropdownOptionsProps): DropDownOptions[] => {
  const baseOptions: DropDownOptions[] = [
    { value: 'AR', label: strings.ACCOUNTS_RECEIVABLE }, // FC2-2679 — re-enabled after DW aging propagation handled (reverts FC2-3136)
    { value: 'FEE', label: strings.FEE },
    { value: 'ACCRUED_FEE', label: strings.ACCRUED_FEE },
    { value: 'ESCROW', label: strings.ESCROW_RESERVE },
    { value: 'CASH_RESERVE', label: strings.CASH_RESERVE },
    { value: 'ACCRUED_INTEREST', label: strings.ACCRUED_INTEREST },
    { value: 'AP_BANK_ACCOUNT', label: strings.AP_BANK_ACCOUNT },
    { value: 'AR_BANK_ACCOUNT', label: strings.AR_BANK_ACCOUNT },
    { value: 'LIABILITY', label: strings.LIABILITY },
    { value: 'UNAPPLIED', label: strings.UNAPPLIED },
  ]

  if (props.canCreateWriteOffs === true) {
    baseOptions.push({ value: 'WRITE_OFFS', label: strings.WRITE_OFFS })
  }

  if (props.canDisplayOverAdvances === true) {
    baseOptions.push({ value: 'OVER_ADVANCE', label: strings.OVER_ADVANCE })
  }

  return baseOptions
}

export const billingDeliveryDropdownOptions: DropDownOptions[] = [
  { value: 'EMAIL', label: 'Email' },
  { value: 'MAIL', label: 'Mail' },
  { value: 'PORTAL', label: 'Portal' },
  { value: 'BILL360', label: 'Bill 360' },
]

export const statementDeliveryDropdownOptions: DropDownOptions[] = [
  { value: 'DAILY', label: 'Daily' },
  { value: 'WEEKLY', label: 'Weekly' },
  { value: 'MONTHLY', label: 'Monthly' },
]

export const invoiceGroupTypeDropdownOptions: DropDownOptions[] = [
  { value: 'INVOICE', label: strings.INVOICE },
  { value: 'FUEL_ADVANCE', label: strings.FUEL_ADVANCE },
  { value: 'RESERVE_RELEASE', label: strings.RESERVE_RELEASE },
  { value: 'OVER_ADVANCE', label: strings.OVER_ADVANCE },
]

export const paginationLimitOptions: DropDownOptions[] = [
  { label: '20', value: '20' },
  { label: '50', value: '50' },
  { label: '100', value: '100' },
]

export const paginationLimitOptionsHighVolume: DropDownOptions[] = [
  { label: '20', value: '20' },
  { label: '50', value: '50' },
  { label: '100', value: '100' },
  { label: '250', value: '250' },
  { label: '500', value: '500' },
]

export const paginationLimitValues: number[] = paginationLimitOptions.map((o) => Number(o.value))
export const paginationLimitValuesHighVolume: number[] = paginationLimitOptionsHighVolume.map((o) => Number(o.value))

export const getImportCsvFileOptions = (enableFuelAdvanceCredit: boolean): DropDownOptions[] => {
  const options: DropDownOptions[] = [
    { label: 'Clients', value: 'CLIENTS' },
    { label: 'Debtors', value: 'DEBTORS' },
    { label: 'Brokers', value: 'BROKERS' },
    { label: 'Vendors', value: 'VENDORS' },
    { label: 'Funding Instructions', value: 'FUNDING_INSTRUCTIONS' },
    { label: 'Fuel Advances', value: 'FUEL_ADVANCES_INVOICE' },
    { label: 'Invoices (Clients)', value: 'CLIENT_INVOICES' },
    { label: 'Invoices (Brokers)', value: 'BROKER_INVOICES' },
    { label: 'Payments', value: 'PAYMENTS' },
  ]

  if (enableFuelAdvanceCredit) {
    options.splice(6, 0, { label: 'Fuel Advances Credit', value: 'FUEL_ADVANCES_CREDIT' })
  }

  return options
}

export const accountingDocumentOptions: () => DropDownOptions[] = () => {
  const array = [
    { label: strings.IFF_EXPORT, value: 'IFF_EXPORT' },
    { label: strings.ACCOUNTING_EXPORT, value: 'ACCOUNTING_EXPORT' },
    { label: strings.ACCOUNTING_DETAILS, value: 'ACCOUNTING_DETAILS' },
  ]
  if (quickbooksEnabled) {
    array.push(
      { label: strings.QUICKBOOKS_ENTRY_AUDIT, value: 'QUICKBOOKS_ENTRY_AUDIT' },
      { label: strings.PUSH_TO_QUICKBOOKS, value: 'PUSH_TO_QUICKBOOKS' }
    )
  }
  return array
}

export const labelEntityTypeOptions: DropDownOptions[] = [
  { label: strings.ENTITY_TYPE_INVOICE, value: 'INVOICE' },
  { label: strings.ENTITY_TYPE_PAYMENT, value: 'PAYMENT' },
  /*   { label: strings.ENTITY_TYPE_COMPANY, value: 'COMPANY' },
  { label: strings.ENTITY_TYPE_TERM, value: 'TERM' }, */
]

export const labelCategoryOptions: DropDownOptions[] = [{ label: strings.CATEGORY_GENERAL, value: 'GENERAL' }]

export const invoiceLabelCategoryOptions: DropDownOptions[] = [
  { label: strings.CATEGORY_INVOICE_COLLECTIONS, value: 'INVOICE_COLLECTIONS' },
  { label: strings.CATEGORY_INVOICE_CHARGEBACK, value: 'INVOICE_CHARGEBACK' },
  { label: strings.CATEGORY_INVOICE_PAYMENT, value: 'INVOICE_PAYMENT' },
]

export const paymentLabelCategoryOptions: DropDownOptions[] = []

export const industryTypeDropDownOptions: DropDownOptions[] = [
  { label: 'General', value: 'GENERAL' as IndustryType },
  { label: 'Transportation', value: 'TRANSPORTATION' as IndustryType },
]

export const getIndustryType = (value: string | undefined | null): IndustryType => {
  if (value === 'TRANSPORTATION') return 'TRANSPORTATION'
  return 'GENERAL'
}

export const shouldShowTransportationFields = (industryType: string | undefined | null): boolean => {
  return getIndustryType(industryType) === 'TRANSPORTATION'
}

export const basePaymentMethodOptions: DropDownOptions[] = [
  { value: '', label: '-' },
  { value: 'FACTOR_CLOUD', label: 'Factor Cloud' },
  { value: 'TANK', label: 'Tank' },
  { value: 'ATOB', label: 'AtoB' },
  { value: 'BANKSHOT', label: 'Bankshot' },
  { value: 'TEXAS_CAPITAL', label: 'Texas Capital' },
]

export const PAYMENT_METHOD_RESTRICTIONS = {
  TANK: ['ACH', 'SAME_DAY_ACH', 'WIRE', 'DIGITAL_WALLET', 'FUEL_CARD', 'INSTANT', 'RTP'],
  ATOB: [],
  BANKSHOT: ['ACH', 'SAME_DAY_ACH', 'WIRE'],
  TEXAS_CAPITAL: ['ACH', 'SAME_DAY_ACH', 'WIRE', 'INSTANT', 'RTP'],
  FACTOR_CLOUD: [],
} as const

export const PAYMENT_SETUP_RESTRICTIONS = {
  TANK: [
    'achPreferredPaymentChannel',
    'sameDayAchPreferredPaymentChannel',
    'wirePreferredPaymentChannel',
    'digitalWalletPreferredPaymentChannel',
    'fuelCardPreferredPaymentChannel',
    'instantPreferredPaymentChannel',
    'rtpPreferredPaymentChannel',
  ],
  ATOB: [],
  BANKSHOT: ['achPreferredPaymentChannel', 'sameDayAchPreferredPaymentChannel', 'wirePreferredPaymentChannel'],
  TEXAS_CAPITAL: [
    'achPreferredPaymentChannel',
    'sameDayAchPreferredPaymentChannel',
    'wirePreferredPaymentChannel',
    'instantPreferredPaymentChannel',
    'rtpPreferredPaymentChannel',
  ],
  FACTOR_CLOUD: [],
} as const

export const getFilteredOptions = (
  currentPaymentMethod: string,
  isEnabledTank: boolean,
  isEnabledAtob: boolean,
  isEnabledBankshot: boolean,
  isEnabledTexasCapital: boolean,
  isClient: boolean
): DropDownOptions[] => {
  return basePaymentMethodOptions.filter((option) => {
    if (option.value === '' || option.value === 'FACTOR_CLOUD') return true
    if (!isClient && (option.value === 'TANK' || option.value === 'BANKSHOT' || option.value === 'TEXAS_CAPITAL'))
      return false
    if (option.value === 'TANK' && !isEnabledTank) return false
    if (option.value === 'ATOB' && !isEnabledAtob) return false
    if (option.value === 'BANKSHOT' && !isEnabledBankshot) return false
    if (option.value === 'TEXAS_CAPITAL' && !isEnabledTexasCapital) return false

    if (option.value === 'TANK') {
      return PAYMENT_METHOD_RESTRICTIONS.TANK.includes(currentPaymentMethod as any)
    }
    if (option.value === 'ATOB') {
      return true
    }
    if (option.value === 'BANKSHOT') {
      return PAYMENT_METHOD_RESTRICTIONS.BANKSHOT.includes(currentPaymentMethod as any)
    }
    if (option.value === 'TEXAS_CAPITAL') {
      return PAYMENT_METHOD_RESTRICTIONS.TEXAS_CAPITAL.includes(currentPaymentMethod as any)
    }

    return true
  })
}

export const getPaymentSetupOptions = (
  channelKey: ChannelKey,
  isEnabledTank: boolean,
  isEnabledAtob: boolean,
  isEnabledBankshot: boolean,
  isEnabledTexasCapital: boolean
): DropDownOptions[] => {
  return basePaymentMethodOptions.filter((option) => {
    if (option.value === '' || option.value === 'FACTOR_CLOUD') return true
    if (option.value === 'TANK' && !isEnabledTank) return false
    if (option.value === 'ATOB' && !isEnabledAtob) return false
    if (option.value === 'BANKSHOT' && !isEnabledBankshot) return false
    if (option.value === 'TEXAS_CAPITAL' && !isEnabledTexasCapital) return false

    if (option.value === 'TANK') {
      return PAYMENT_SETUP_RESTRICTIONS.TANK.includes(channelKey as any)
    }
    if (option.value === 'ATOB') {
      return true
    }
    if (option.value === 'BANKSHOT') {
      return PAYMENT_SETUP_RESTRICTIONS.BANKSHOT.includes(channelKey as any)
    }
    if (option.value === 'TEXAS_CAPITAL') {
      return PAYMENT_SETUP_RESTRICTIONS.TEXAS_CAPITAL.includes(channelKey as any)
    }

    return true
  })
}

export type ChannelKey =
  | 'achPreferredPaymentChannel'
  | 'checkPreferredPaymentChannel'
  | 'expressCodePreferredPaymentChannel'
  | 'fuelCardPreferredPaymentChannel'
  | 'sameDayAchPreferredPaymentChannel'
  | 'wirePreferredPaymentChannel'
  | 'moneyCodePreferredPaymentChannel'
  | 'digitalWalletPreferredPaymentChannel'
  | 'instantPreferredPaymentChannel'
  | 'rtpPreferredPaymentChannel'

export const NoaLevelSettingDropdownOption = [
  { value: 'CLIENT_LEVEL', label: strings.CLIENT_LEVEL },
  { value: 'INVOICE_LEVEL', label: strings.INVOICE_LEVEL },
]

export const NoaPlacementSettingDropdownOption = [
  { value: 'FIRST', label: strings.NOA_PLACEMENT_FIRST },
  { value: 'AFTER_FC_INVOICE', label: strings.NOA_PLACEMENT_AFTER_FC_INVOICE },
  { value: 'LAST', label: strings.NOA_PLACEMENT_LAST },
]

export const NoaPlacementSettingDebtorDropdownOption = [
  { value: '', label: strings.FACTOR_DEFAULT },
  ...NoaPlacementSettingDropdownOption,
]

export const CollectionFeeMethodDropdownOption = [
  { value: 'CASH_BASED', label: strings.CASH_BASED },
  { value: 'ACCRUAL_BASED', label: strings.ACCRUAL_BASED },
]


export const NachaVersionDropdownOption = [
  { value: '1', label: strings.NACHA_VERSION_1 },
  { value: '2', label: strings.NACHA_VERSION_2 },
  { value: '3', label: strings.NACHA_VERSION_3 },
]

export const TractIncomeLevelsOptions = [
  { value: '', label: '-' },
  { value: 'LOW_INCOME', label: strings.LOW_INCOME },
  { value: 'MODERATE_INCOME', label: strings.MODERATE_INCOME },
  { value: 'MIDDLE_INCOME', label: strings.MIDDLE_INCOME },
  { value: 'UPPER_INCOME', label: strings.UPPER_INCOME },
]

export const PaymentMethodOptions: DropDownOptions[] = [
  { value: 'SPLIT', label: strings.SPLIT },
  { value: 'ACH', label: strings.ACH },
  { value: 'CHECK', label: strings.CHECK },
  { value: 'DIGITAL_WALLET', label: strings.DIGITAL_WALLET },
  { value: 'FUEL_CARD', label: strings.FUEL_CARD },
  { value: 'INSTANT', label: strings.INSTANT },
  { value: 'MONEY_CODE', label: strings.MONEY_CODE },
  { value: 'RTP', label: strings.RTP },
  { value: 'SAME_DAY_ACH', label: strings.SAME_DAY_ACH },
  { value: 'WIRE', label: strings.WIRE },
]

export const collectionStatusColourOptions: DropDownOptions[] = [
  { value: 'RED', label: 'Red' },
  { value: 'YELLOW', label: 'Yellow' },
  { value: 'GREEN', label: 'Green' },
  { value: 'BLUE', label: 'Blue' },
]
